import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';

import type { ICredentialSourcePort } from '@identity/application/ports';
import { linkTokenCookieStorage, sessionCredentialSource } from '@identity/infrastructure/storage';

const baseHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'Accept-Language': 'es-MX',
};

/**
 * A bare instance for `parrot-api`, with no credential attached yet.
 *
 * The environment variable names the host only, so the `/api` prefix belongs to the instance and the
 * routes stay free of it.
 */
const createIdentityInstance = (): AxiosInstance =>
  axios.create({
    baseURL: `${process.env.REACT_APP_BASE_URL_API}/api`,
    headers: baseHeaders,
  });

/**
 * Injects the credential of this flow on every request that does not bring its own.
 *
 * The token is read again on every call, so a late login or a rotation reaches the next request.
 *
 * A request that already carries an `Authorization` header keeps it: the consume authorizes with the
 * token of the magic link, and overwriting it would consume the stored one instead.
 *
 * A request without a token is sent unauthorized, which is what lets the caller tell "no credential"
 * apart from "invalid credential".
 */
export const attachCredentialInterceptor = (
  instance: AxiosInstance,
  credentials: ICredentialSourcePort,
): AxiosInstance => {
  instance.interceptors.request.use((config: AxiosRequestConfig) => {
    if (config.headers?.Authorization) return config;

    const token = credentials.read();

    if (token) {
      config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
    }

    return config;
  });

  return instance;
};

/**
 * Authorized with the link token, for the public routes.
 *
 * It never falls back to the session: an owner who opens the invitation link of a new employee has
 * one open, and using it would apply the invitation to the wrong account.
 */
export const identityLinkApi = attachCredentialInterceptor(createIdentityInstance(), linkTokenCookieStorage);

/**
 * Authorized with the Parrot session, for the private routes and the gate.
 *
 * Two instances rather than one with two interceptors: a use case built with this one has no way to
 * send the link token, so the separation holds by construction.
 */
export const identitySessionApi = attachCredentialInterceptor(createIdentityInstance(), sessionCredentialSource);

/**
 * Unauthorized, for the one endpoint of this domain that is open.
 *
 * It has no interceptor, so it has no way to send a credential: a browser that abandoned an
 * invitation still holds the link cookie, and sending that dead token to an open endpoint would meet
 * whoever is trying to recover their access with a `401` from a flow they already forgot.
 */
export const identityPublicApi = createIdentityInstance();
